import { get } from 'object-path-immutable';
import { revert } from 'entities/step-groups';
import { generateElementTemplate } from 'components/Editor/Templates';
import { transformValueFromThemeFormat } from './content/utils';
import {
  themeFilterPrefix,
  extractStylesForThemeVariant,
  applyThemeStyles,
  updateToThemed,
  EDITOR_MAPPING,
} from './themes';

/**
 * Applies a raw theme component to an object
 *
 * @param {Object} componentDefinition - Raw theme component definition
 * @param {string} variantName - Name of the variant to apply. Must match the theme value of `chunk`
 * @param {*} chunk - Chunk (object) to apply theme to
 * @returns {Object} Combined object with theme styles merged with base object
 */
const applyExtractedTheme = (componentDefinition, variantName, chunk) => {
  if (!componentDefinition || !chunk || typeof chunk !== 'object') {
    return chunk;
  }

  if (Array.isArray(chunk)) {
    return chunk.map(item =>
      applyExtractedTheme(componentDefinition, variantName, item)
    );
  }

  const themeStyles = () => {
    if (!chunk.theme?.startsWith(variantName)) {
      return {};
    }

    const style =
      get(componentDefinition, chunk.theme.slice(variantName.length + 1))
        .$style || {};

    return Object.fromEntries(
      Object.entries(style).map(([key, value]) => [
        key,
        transformValueFromThemeFormat(value),
      ])
    );
  };

  const themedChunk = {
    ...chunk,
    ...themeStyles(),
  };

  const themed = Object.fromEntries(
    Object.entries(themedChunk).filter(([, value]) => value !== undefined)
  );

  // Recursively apply theme to all nested objects
  Object.keys(themed).forEach(key => {
    if (themed[key] && typeof themed[key] === 'object') {
      themed[key] = applyExtractedTheme(
        componentDefinition,
        variantName,
        themed[key]
      );
    }
  });

  return themed;
};

const containerReferenceModels = ({
  compiledTheme,
  type,
  chunk: editor,
  currentVariant,
}) => {
  const originalEditor = applyThemeStyles(
    compiledTheme,
    updateToThemed({
      type,
      content: editor,
      selectedVariant: currentVariant,
    })
  );
  const { traits: originalTraits } = revert({
    editor: originalEditor,
    traits: [],
  });
  const { traits } = revert({ editor, traits: [] });

  return {
    originalReferenceModel: { traits: originalTraits },
    referenceModel: { traits },
  };
};

const blockReferenceModels = ({
  compiledTheme,
  type,
  currentVariant,
  componentDefinition,
}) => {
  // We want to show a preview of the styles, but not the specific content of the block with the styles
  // to make it clear that the content is not saved as part of the theme
  const templateContent = generateElementTemplate({
    template: type,
    blockId: 'block-id',
    elementId: 'element-id',
  }).content;
  const referenceContent = applyThemeStyles(
    compiledTheme,
    updateToThemed({
      type,
      content: templateContent,
      selectedVariant: currentVariant,
    })
  );
  const referenceModel = applyExtractedTheme(
    componentDefinition,
    currentVariant,
    referenceContent
  );

  return {
    originalReferenceModel: { block: referenceContent },
    referenceModel: { block: referenceModel },
  };
};

export const donateComponent = ({ compiledTheme, type, chunk, isCreate }) => {
  const currentVariant = chunk.style?.theme;
  const componentDefinition = extractStylesForThemeVariant({
    compiledTheme,
    type,
    content: chunk,
  });

  const { referenceModel, originalReferenceModel } = (
    type === EDITOR_MAPPING ? containerReferenceModels : blockReferenceModels
  )({
    compiledTheme,
    type,
    chunk,
    currentVariant,
    componentDefinition,
  });

  return {
    isCreate,
    type: themeFilterPrefix(type === EDITOR_MAPPING ? chunk.type : type),
    existingPath: currentVariant,
    referenceModel,
    ...(!isCreate && { originalReferenceModel }),
    componentDefinition,
  };
};
